import React, { useCallback, useMemo, useState } from "react";
import getConfig from "next/config";
import styled, { css } from "styled-components";
import clsx from "clsx";
import { isMobile } from "react-device-detect";
import { components } from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import { OverlayTrigger, Tooltip, Form } from "react-bootstrap";

import { hexToRGBA } from "../../../utils/theme";

const { publicRuntimeConfig } = getConfig();

const StyledGroup = styled(Form.Group)`
  position: relative;

  width: 100%;

  padding-left: 0;
  padding-right: 0;

  ${({ withLabel }) => withLabel && css`
    padding-top: 24px;
  `};

  .async-select-container {
    position: relative;
  }

  .async-select-container .async-select__control,
  .async-select-container .async-select-valid__control,
  .async-select-container .async-select-invalid__control {
    border: none;
    border-radius: 7px;
    background-color: ${hexToRGBA('#078FF1', 0.1)};
    box-shadow: none;
  }

  .async-select-container .async-select__control .async-select__indicator,
  .async-select-container .async-select-valid__control .async-select-valid__indicator,
  .async-select-container .async-select-invalid__control .async-select-invalid__indicator {
    padding: 0;
    position: absolute;
    right: 16px;
  }

  .async-select-container .async-select-valid__control .async-select-valid__indicator,
  .async-select-container .async-select-invalid__control .async-select-invalid__indicator {
    right: 12px;
    height: calc(0.75rem + 0.375rem);
    width: calc(0.75rem + 0.375rem);
  }

  .async-select-container .async-select__control .async-select__indicator.async-select__clear-indicator {
    margin-right: 16px;
  }

  .async-select-container .async-select-valid__control .async-select-valid__indicator.async-select-valid__clear-indicator,
  .async-select-container .async-select-invalid__control .async-select-invalid__indicator.async-select-invalid__clear-indicator {
    margin-right: 22px;
  }

  .async-select-container .async-select__control .async-select__indicator.async-select__loading-indicator {
    margin-right: 20px;
    color: #078FF1;
  }

  .async-select-container .async-select-valid__control .async-select-valid__indicator.async-select-valid__loading-indicator,
  .async-select-container .async-select-invalid__control .async-select-invalid__indicator.async-select-invalid__loading-indicator {
    margin-top: 18px;
    margin-right: 28px;
  }

  .async-select-container .async-select__control .async-select__indicator.async-select__clear-indicator,
  .async-select-container .async-select-valid__control .async-select-valid__indicator.async-select-valid__clear-indicator,
  .async-select-container .async-select-invalid__control .async-select-invalid__indicator.async-select-invalid__clear-indicator {
    color: #078FF1;
    cursor: pointer;
  }

  .async-select-container .async-select__control .async-select__indicator-separator,
  .async-select-container .async-select-valid__control .async-select-valid__indicator-separator,
  .async-select-container .async-select-invalid__control .async-select-invalid__indicator-separator {
    display: none;
  }

  .async-select-container .async-select__control .async-select__value-container,
  .async-select-container .async-select-valid__control .async-select-valid__value-container,
  .async-select-container .async-select-invalid__control .async-select-invalid__value-container {
    height: 52px;
    padding-top: 8px;
    padding-bottom: 8px;
    padding-right: 52px;
    padding-left: 16px;

    .async-select__single-value,
    .async-select__input-container {
      font-family: 'LatoRegular';
      font-size: 14px;
      line-height: 24px;
      color: ${hexToRGBA('#1B3858', 0.5)};
      caret-color: ${hexToRGBA('#1B3858', 0.5)};
    }
  }

  .async-select-container .async-select-valid__control .async-select-valid__value-container {
    border-radius: 7px;
    border: 1px solid #198754 !important;
  }

  .async-select-container .async-select-invalid__control .async-select-invalid__value-container {
    border-radius: 7px;
    border: 1px solid #dc3545 !important;
  }

  .async-select-container .async-select__control.async-select__control--is-focused .async-select__value-container,
  .async-select-container .async-select-valid__control.async-select-valid__control--is-focused .async-select-valid__value-container,
  .async-select-container .async-select-invalid__control.async-select-invalid__control--is-focused .async-select-invalid__value-container {
    border-color: #86b7fe;
  }

  .async-select-container .async-select__control .async-select__value-container .async-select__input-container,
  .async-select-container .async-select-valid__control .async-select-valid__value-container .async-select-valid__input-container,
  .async-select-container .async-select-invalid__control .async-select-invalid__value-container .async-select-invalid__input-container {
    padding: 0;
    margin: 0;
  }
`;

const StyledLabel = styled(Form.Label)`
  position: absolute;
  left: 0;
  top: 0;
  font-family: 'LatoBold';
  font-size: 12px;
  line-height: 24px;
  text-transform: uppercase;
  white-space: nowrap;
  color: #078FF1;

  span {
    font-size: 16px;
    color: #C61F0C;
  }
`;

const StyledFeedback = styled(Form.Control.Feedback)``;

function AsyncSelect({
  refreshKey,
  id,
  hint,
  label,
  loading,
  emptyLabel,
  placeholder,
  required,
  validated,
  defaultInvalidMessage = "",
  loadOptions,
  value = null,
  clazz = null,
  onChange,
  ...restProps
}) {
  const [searchInputValue, setSearchInputValue] = useState("");

  const handleChange = useCallback((option) => {
    setSearchInputValue("");
    onChange && onChange({ target: { id, value: option?.value, clazz: option?.clazz, } });
  }, [onChange]);

  const handleSearchChange = (value) => {
    setSearchInputValue(prevValue => {
      if (prevValue?.length === 1 && !value || value) {
        return value;
      }

      return prevValue;
    });
  }

  const classNamePrefix = clsx({
    ["async-select"]: !validated || !required && validated && !value,
    ["async-select-valid"]: validated && value,
    ["async-select-invalid"]: required && validated && !value,
  });

  const LoadingIndicator = (props) => <components.LoadingIndicator {...props} />

  const DropdownIndicator = props => {
    return (
      <components.DropdownIndicator {...props}>
        {(!validated || !required && validated && !value) && (
          <img src={`${publicRuntimeConfig.basePath}/icons/V2/caret_down.svg`} alt="" />
        )}
        {validated && value && (
          <img src={`${publicRuntimeConfig.basePath}/icons/V2/validation_success.svg`} alt="" />
        )}
        {required && validated && !value && (
          <img src={`${publicRuntimeConfig.basePath}/icons/V2/validation_failure.svg`} alt="" />
        )}
      </components.DropdownIndicator>
    );
  };

  const overlayTriggerProps = useMemo(() => {
    const props = {
      placement: isMobile ? "top" : "right-start",
      delay: { show: 0, hide: 0 },
      trigger: ["hover", "focus"],
      overlay: (
        <Tooltip id={`tooltip-${id}`} className="hintTooltip">
          {hint}
        </Tooltip>
      ),
    };

    if (!hint || isMobile) {
      props.show = false;
    }

    return props;
  }, [id, hint]);

  const isValid = value && !loading || !required;
  const isInvalid = required && !value || required && loading;

  return (
    <OverlayTrigger {...overlayTriggerProps}>
      <StyledGroup
        controlId={id} 
        withLabel={Boolean(label || emptyLabel)}
      >
        <StyledLabel>
          {label}
          {!emptyLabel && label && required && (<span>&nbsp;*</span>)}
        </StyledLabel>
        <Form.Control
          required
          readOnly
          hidden
          isValid={isValid}
          isInvalid={isInvalid}
          {...restProps}
        />
        <AsyncPaginate
          key={refreshKey}
          className="async-select-container"
          classNamePrefix={classNamePrefix}
          placeholder={placeholder}
          components={{ DropdownIndicator, LoadingIndicator }}
          isLoading={loading}
          isClearable={!!value}
          additional={{ page: 1 }}
          loadOptions={loadOptions}
          inputValue={searchInputValue}
          debounceTimeout={500}
          value={value ? { label: value, value: value } : { label: placeholder }}
          onInputChange={handleSearchChange}          
          onChange={handleChange}
        />
        <StyledFeedback type="invalid">
          {defaultInvalidMessage}
        </StyledFeedback>
      </StyledGroup>
    </OverlayTrigger>
  );
}

export default AsyncSelect;
